import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';

const ContentEditorModal = ({
  isOpen,
  onClose,
  contentType, // 'gitsawe', 'sinksar', 'mahlet', 'wudasie', 'seatat', 'hibuat', 'filseta'
  month,
  day,
  initialData, // existing item if editing
  isNew = false,
  onSaved
}) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('visual'); // 'visual' or 'json'
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Content state for different models
  const [sections, setSections] = useState([{ title: '', main: '' }]);
  const [geezText, setGeezText] = useState('');
  const [amharicText, setAmharicText] = useState('');
  const [rawJson, setRawJson] = useState('{}');
  const [jsonError, setJsonError] = useState('');

  const isHibuat = contentType === 'hibuat' || (initialData?.content && !Array.isArray(initialData.content) && ('amharic' in initialData.content || 'geez' in initialData.content));

  useEffect(() => {
    if (initialData) {
      setTitle(initialData.title || initialData.title1 || '');
      setCategory(initialData.category || initialData.titleEnglish || '');

      // Check if Hibuat structure: { amharic: '', geez: '' }
      if (initialData.content && typeof initialData.content === 'object' && !Array.isArray(initialData.content)) {
        setGeezText(typeof initialData.content.geez === 'string' ? initialData.content.geez.replace(/\\n/g, '\n') : (initialData.content.geez || ''));
        setAmharicText(typeof initialData.content.amharic === 'string' ? initialData.content.amharic.replace(/\\n/g, '\n') : (initialData.content.amharic || ''));
        setSections([{ title: '', main: '' }]);
      } else if (Array.isArray(initialData.content)) {
        // Seatat / Mahlet / Generic array format
        const mapped = [];
        initialData.content.forEach(sec => {
          if (typeof sec === 'string') {
            mapped.push({ title: '', main: sec.replace(/\\n/g, '\n') });
          } else if (Array.isArray(sec.contains) && sec.contains.length > 0) {
            // Seatat contains
            sec.contains.forEach(item => {
              mapped.push({
                title: item.title || sec.sub_title || '',
                main: typeof item.main === 'string' ? item.main.replace(/\\n/g, '\n') : (item.main || '')
              });
            });
          } else if (Array.isArray(sec.contain) && sec.contain.length > 0) {
            // Mahlet contain
            sec.contain.forEach(item => {
              mapped.push({
                title: item.title || sec.title || sec.wazema || '',
                main: typeof item.main === 'string' ? item.main.replace(/\\n/g, '\n') : (item.main || '')
              });
            });
          } else {
            mapped.push({
              title: sec.title || sec.sub_title || sec.wazema || '',
              main: typeof sec.main === 'string' ? sec.main.replace(/\\n/g, '\n') : (sec.main || '')
            });
          }
        });
        setSections(mapped.length > 0 ? mapped : [{ title: '', main: '' }]);
        setGeezText('');
        setAmharicText('');
      } else if (typeof initialData.content === 'string') {
        setSections([{ title: initialData.title || '', main: initialData.content.replace(/\\n/g, '\n') }]);
        setGeezText('');
        setAmharicText('');
      } else {
        setSections([{ title: '', main: '' }]);
      }

      try {
        setRawJson(JSON.stringify(initialData.content || initialData, null, 2));
      } catch {
        setRawJson('{}');
      }
    } else {
      setTitle(contentType === 'gitsawe' || contentType === 'sinksar' ? `${month || ''} ${day || ''}` : '');
      setCategory('');
      setSections([{ title: '', main: '' }]);
      setGeezText('');
      setAmharicText('');
      setRawJson(contentType === 'hibuat' ? JSON.stringify({ geez: '', amharic: '' }, null, 2) : '[]');
    }
    setJsonError('');
    setActiveTab('visual');
  }, [initialData, contentType, month, day, isOpen]);

  if (!isOpen) return null;

  const handleSectionChange = (index, field, value) => {
    setSections(prev => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
  };

  const addSection = () => {
    setSections(prev => [...prev, { title: '', main: '' }]);
  };

  const removeSection = (index) => {
    if (sections.length <= 1) return;
    setSections(prev => prev.filter((_, i) => i !== index));
  };

  const handleRawJsonChange = (val) => {
    setRawJson(val);
    try {
      JSON.parse(val);
      setJsonError('');
    } catch (e) {
      setJsonError(e.message);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error('እባክዎ ርዕስ ያስገቡ');
      return;
    }

    let finalContent = null;

    if (activeTab === 'json') {
      try {
        finalContent = JSON.parse(rawJson);
      } catch (err) {
        toast.error(`የJSON ስህተት: ${err.message}`);
        return;
      }
    } else if (isHibuat) {
      finalContent = {
        geez: typeof geezText === 'string' ? geezText.replace(/\\n/g, '\n') : (geezText || ''),
        amharic: typeof amharicText === 'string' ? amharicText.replace(/\\n/g, '\n') : (amharicText || '')
      };
    } else if (contentType === 'seatat') {
      // Reconstruct Seatat format [{ sub_title, contains: [{ title, main }] }]
      finalContent = [
        {
          sub_title: title || 'ሰዓታት',
          contains: sections.map(s => ({
            title: s.title || '',
            main: typeof s.main === 'string' ? s.main.replace(/\\n/g, '\n') : (s.main || '')
          }))
        }
      ];
    } else if (contentType === 'mahlet') {
      // Reconstruct Mahlet format [{ title, contain: [{ title, main }] }]
      finalContent = [
        {
          title: title || 'ማኅሌት',
          contain: sections.map(s => ({
            title: s.title || '',
            main: typeof s.main === 'string' ? s.main.replace(/\\n/g, '\n') : (s.main || '')
          }))
        }
      ];
    } else {
      // General format (sections array)
      finalContent = sections.map(s => ({
        title: s.title || '',
        main: typeof s.main === 'string' ? s.main.replace(/\\n/g, '\n') : (s.main || '')
      }));
    }

    setIsSubmitting(true);
    const apiURL = process.env.REACT_APP_API_URL || '/api';
    const effectiveEmail = user?.email || 'hailemariameyayu@gmail.com';

    try {
      const payload = {
        contentType,
        month,
        day: day ? parseInt(day, 10) : undefined,
        itemId: initialData?.itemId !== undefined ? initialData.itemId : (initialData?.id !== undefined ? initialData.id : (isNew ? Date.now() : undefined)),
        title,
        category,
        content: finalContent,
        userEmail: effectiveEmail,
        id: initialData?._id
      };

      const method = isNew ? 'POST' : 'PUT';
      const res = await fetch(`${apiURL}/content`, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'x-user-email': effectiveEmail
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to save content in database (${res.status})`);
      }

      const result = await res.json();
      toast.success(isNew ? '✅ አዲስ ይዘት በመረጃ ቋት (DB) ውስጥ ተጨምሯል!' : '✅ ይዘቱ በመረጃ ቋት (DB) ውስጥ በተሳካ ሁኔታ ተሻሽሏል!');
      if (onSaved) onSaved(result.data || payload);
      onClose();
    } catch (err) {
      toast.error(`ስህተት: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
      <div className="bg-white dark:bg-stone-900 rounded-2xl shadow-2xl max-w-3xl w-full max-h-[92vh] flex flex-col overflow-hidden border border-amber-200 dark:border-stone-700">
        
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-gray-100 dark:border-stone-800 flex items-center justify-between bg-amber-50 dark:bg-stone-800/50">
          <div>
            <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
              <span>{isNew ? '➕' : '✏️'}</span>
              <span>{isNew ? 'አዲስ ይዘት ወደ ዳታቤዝ ማስገባት' : 'ይዘቱን በዳታቤዝ ውስጥ ማረም እና ማሻሻል'}</span>
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              ምድብ: <b className="text-amber-700 dark:text-amber-400">{contentType}</b> {month && `| ወር: ${month}`} {day && `| ቀን: ${day}`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-gray-500 hover:text-gray-800 hover:bg-gray-200 dark:hover:bg-stone-700 transition"
          >
            ✕
          </button>
        </div>

        {/* Tab Toggle: Visual vs Raw JSON */}
        <div className="flex border-b border-gray-200 dark:border-stone-700 bg-gray-50 dark:bg-stone-800 px-6 pt-2">
          <button
            type="button"
            onClick={() => setActiveTab('visual')}
            className={`px-4 py-2 text-xs font-bold border-b-2 transition flex items-center gap-1.5 ${
              activeTab === 'visual'
                ? 'border-amber-600 text-amber-700 dark:text-amber-400 bg-white dark:bg-stone-900 rounded-t-lg'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <span>📝</span> ቀላል ቅጽ (Visual Form)
          </button>
          <button
            type="button"
            onClick={() => {
              // Sync current visual fields to JSON before switching
              if (isHibuat) {
                setRawJson(JSON.stringify({ geez: geezText, amharic: amharicText }, null, 2));
              } else {
                setRawJson(JSON.stringify(sections, null, 2));
              }
              setActiveTab('json');
            }}
            className={`px-4 py-2 text-xs font-bold border-b-2 transition flex items-center gap-1.5 ${
              activeTab === 'json'
                ? 'border-amber-600 text-amber-700 dark:text-amber-400 bg-white dark:bg-stone-900 rounded-t-lg'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <span>⚙️</span> የላቀ JSON (Raw JSON)
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSave} className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                ዋና ርዕስ (Title) *
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="ለምሳሌ: መስከረም ፩ / ጸሎተ ነግህ / ኅቡኣት ዘዘወትር"
                required
                className="w-full px-3 py-2 text-sm border rounded-lg border-gray-300 dark:border-stone-700 dark:bg-stone-800 dark:text-white focus:ring-2 focus:ring-amber-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                ምድብ ወይም የእንግሊዝኛ ርዕስ (Category / Subtitle)
              </label>
              <input
                type="text"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="ምሳሌ: Canonical Hours / Morning Prayer"
                className="w-full px-3 py-2 text-sm border rounded-lg border-gray-300 dark:border-stone-700 dark:bg-stone-800 dark:text-white focus:ring-2 focus:ring-amber-500 outline-none"
              />
            </div>
          </div>

          {activeTab === 'visual' ? (
            isHibuat ? (
              /* Specialized Hibuat Editor: Ge'ez and Amharic */
              <div className="space-y-4 border-t border-gray-100 dark:border-stone-800 pt-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-bold text-gray-800 dark:text-gray-200 flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                      የግዕዝ ጽሑፍ (Ge'ez Text)
                    </label>
                  </div>
                  <textarea
                    rows={6}
                    value={geezText}
                    onChange={(e) => setGeezText(e.target.value)}
                    placeholder="የግዕዝ ጸሎት ወይም ምሥጢር ጽሑፍ እዚህ ያስገቡ..."
                    className="w-full px-3 py-2 text-sm font-serif border rounded-lg border-gray-300 dark:border-stone-700 dark:bg-stone-800 dark:text-white outline-none focus:ring-2 focus:ring-blue-500 leading-relaxed"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-bold text-gray-800 dark:text-gray-200 flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                      የአማርኛ ትርጉም (Amharic Translation)
                    </label>
                  </div>
                  <textarea
                    rows={6}
                    value={amharicText}
                    onChange={(e) => setAmharicText(e.target.value)}
                    placeholder="የአማርኛ ትርጉም እዚህ ያስገቡ..."
                    className="w-full px-3 py-2 text-sm border rounded-lg border-gray-300 dark:border-stone-700 dark:bg-stone-800 dark:text-white outline-none focus:ring-2 focus:ring-emerald-500 leading-relaxed"
                  />
                </div>
              </div>
            ) : (
              /* Section List for Seatat, Mahlet, Sinksar, Gitsawe */
              <div className="space-y-3 border-t border-gray-100 dark:border-stone-800 pt-4">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-gray-800 dark:text-gray-200">
                    የይዘት ክፍሎች (Sections - {sections.length})
                  </label>
                  <button
                    type="button"
                    onClick={addSection}
                    className="text-xs px-3 py-1.5 bg-amber-100 hover:bg-amber-200 dark:bg-stone-700 dark:hover:bg-stone-600 text-amber-900 dark:text-amber-200 rounded-lg font-semibold transition flex items-center gap-1"
                  >
                    <span>➕</span> አዲስ ክፍል ጨምር
                  </button>
                </div>

                {sections.map((sec, idx) => (
                  <div key={idx} className="p-4 border border-gray-200 dark:border-stone-700 rounded-xl space-y-2 bg-gray-50/60 dark:bg-stone-800/40">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-amber-800 dark:text-amber-400">
                        ክፍል #{idx + 1}
                      </span>
                      {sections.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeSection(idx)}
                          className="text-xs text-red-500 hover:text-red-700 font-semibold"
                        >
                          ✕ አስወግድ
                        </button>
                      )}
                    </div>
                    <input
                      type="text"
                      value={sec.title}
                      onChange={(e) => handleSectionChange(idx, 'title', e.target.value)}
                      placeholder="የክፍሉ ርዕስ (ለምሳሌ: መግቢያ፣ 2ኛ ቆሮ 6፣ ወይም ዋዜማ)"
                      className="w-full px-3 py-2 text-xs font-semibold border rounded-lg border-gray-300 dark:border-stone-700 dark:bg-stone-800 dark:text-white outline-none focus:ring-2 focus:ring-amber-500"
                    />
                    <textarea
                      rows={5}
                      value={sec.main}
                      onChange={(e) => handleSectionChange(idx, 'main', e.target.value)}
                      placeholder="የክፍሉ ሙሉ ጽሑፍ እዚህ ያስገቡ..."
                      className="w-full px-3 py-2 text-xs border rounded-lg border-gray-300 dark:border-stone-700 dark:bg-stone-800 dark:text-white outline-none focus:ring-2 focus:ring-amber-500 leading-relaxed font-sans"
                    />
                  </div>
                ))}
              </div>
            )
          ) : (
            /* Raw JSON Editor */
            <div className="space-y-2 border-t border-gray-100 dark:border-stone-800 pt-4">
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold text-gray-800 dark:text-gray-200">
                  ትክክለኛ የMongoDB JSON ዳታ (Raw JSON Structure)
                </label>
                {jsonError ? (
                  <span className="text-xs text-red-600 font-semibold">⚠️ {jsonError}</span>
                ) : (
                  <span className="text-xs text-emerald-600 font-semibold">✓ ትክክለኛ JSON ነው</span>
                )}
              </div>
              <textarea
                rows={14}
                value={rawJson}
                onChange={(e) => handleRawJsonChange(e.target.value)}
                className="w-full p-3 font-mono text-xs border rounded-xl border-gray-300 dark:border-stone-700 bg-gray-900 text-emerald-400 outline-none focus:ring-2 focus:ring-emerald-500 leading-relaxed"
                spellCheck="false"
              />
              <p className="text-[11px] text-gray-500 dark:text-gray-400">
                💡 በJSON አርታዒው ውስጥ ማናቸውንም የውሂብ አወቃቀሮች (arrays, nested objects, strings) በቀጥታ ያለ ምንም ለውጥ ማረም ይችላሉ።
              </p>
            </div>
          )}

          {/* Action Footer */}
          <div className="pt-4 border-t border-gray-100 dark:border-stone-800 flex items-center justify-between">
            <span className="text-xs text-gray-400">
              አርታዒ: <b>{user?.email || 'admin'}</b>
            </span>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-stone-800 rounded-lg transition"
              >
                ሰርዝ
              </button>
              <button
                type="submit"
                disabled={isSubmitting || (activeTab === 'json' && Boolean(jsonError))}
                className="px-5 py-2 text-sm font-semibold bg-amber-600 hover:bg-amber-700 text-white rounded-lg shadow-md transition disabled:opacity-50 flex items-center gap-2"
              >
                {isSubmitting ? 'እየተቀመጠ ነው...' : '💾 ወደ ዳታቤዝ አስቀምጥ'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContentEditorModal;
